import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Image {
  id: string;
  filename: string;
  url: string;
  thumbnailUrl: string;
  category?: string;
}

interface ImageContextType {
  images: Image[];
  getImage: (filename: string) => string | undefined;
  getImageThumbnail: (filename: string) => string | undefined;
  loading: boolean;
  error: string | null;
}

const ImageContext = createContext<ImageContextType | undefined>(undefined);

function getImageApiUrl(): string {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') {
    const { hostname, port } = window.location;
    if (hostname === 'localhost' && port === '3000') return 'http://localhost:4000';
    return ''; // Same-origin when frontend and API share host
  }
  return 'http://localhost:4000';
}

export const ImageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [images, setImages] = useState<Image[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const API_URL = getImageApiUrl();

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/images`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        // Prepend API_URL to image URLs for direct use
        const processedImages = data.images.map((img: any) => ({
          id: img.id,
          filename: img.filename,
          url: `${API_URL}/api/images/${img.id}`,
          thumbnailUrl: `${API_URL}/api/images/${img.id}/thumbnail`,
          category: img.category,
        }));
        setImages(processedImages);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch images');
        console.error('Error fetching images:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [API_URL]);

  const findImageByFilename = (filename: string): Image | undefined => {
    const lower = filename.toLowerCase();
    // Exact match first
    let found = images.find(img => img.filename.toLowerCase() === lower);
    if (found) return found;
    // Fallback: same base name, any extension (.jpg, .png, .webp, etc.)
    const base = filename.replace(/\.[^.]+$/, '').toLowerCase();
    found = images.find(img => {
      const imgBase = img.filename.replace(/\.[^.]+$/, '').toLowerCase();
      return imgBase === base;
    });
    return found;
  };

  const getImage = (filename: string): string | undefined => {
    return findImageByFilename(filename)?.url;
  };

  const getImageThumbnail = (filename: string): string | undefined => {
    return findImageByFilename(filename)?.thumbnailUrl;
  };

  return (
    <ImageContext.Provider value={{ images, getImage, getImageThumbnail, loading, error }}>
      {children}
    </ImageContext.Provider>
  );
};

export const useImages = () => {
  const context = useContext(ImageContext);
  if (context === undefined) {
    throw new Error('useImages must be used within an ImageProvider');
  }
  return context;
};

